import { useSyncExternalStore } from "react";
import { isLaborUnitsEnabled, onLaborUnitsEnabledChange } from "../laborChipTemplate";
import { normalizeLoadTiers } from "./loadTiers";

// Import cycle note: laborChipTemplate -> loadStateFor -> this module -> laborChipTemplate.
// Safe only because every cross-module read happens at call time. Never call
// getGanttChipOptions() or isLaborUnitsEnabled() at module top level.
const flag = (v) => v === true;
let options = null;
const listeners = new Set();

const normalize = (d = {}) => ({
  loadColourEnabled: flag(d.loadColourEnabled),
  loadTiers: normalizeLoadTiers(d.loadTiers),
  nonLaborCostInlineEnabled: flag(d.nonLaborCostInlineEnabled),
  spaceQuantityInlineEnabled: flag(d.spaceQuantityInlineEnabled),
  stockQuantityInlineEnabled: flag(d.stockQuantityInlineEnabled),
  capacitySetupLikelyMissing: flag(d.capacitySetupLikelyMissing),
});

// Unchanged options keep the same reference and notify nobody, so a portfolio
// details refetch repaints no Gantt.
export const setGanttChipOptions = (details) => {
  const next = normalize(details || {});
  if (JSON.stringify(next) === JSON.stringify(getGanttChipOptions())) return;
  options = next;
  listeners.forEach((l) => l(options));
};
// The server answered LOAD_COLOUR_DISABLED: the option was switched off
// since the portfolio was read. Off locally until the next portfolio read.
export const disableLoadColourLocally = () => {
  const current = getGanttChipOptions();
  if (!current.loadColourEnabled) return;
  options = { ...current, loadColourEnabled: false };
  listeners.forEach((l) => l(options));
};
// Cached so the reference is stable between changes (useSyncExternalStore).
export const getGanttChipOptions = () => options || (options = normalize());
export const onGanttChipOptionsChange = (listener) => {
  listeners.add(listener);
  const offLabor = onLaborUnitsEnabledChange(() => listener(getGanttChipOptions()));
  return () => {
    listeners.delete(listener);
    offLabor();
  };
};

export const useGanttChipOptions = () =>
  useSyncExternalStore(onGanttChipOptionsChange, getGanttChipOptions);
export const isInlineEditEnabled = (dimension) => {
  const o = getGanttChipOptions();
  switch (dimension) {
    case "labor": return isLaborUnitsEnabled();
    case "non-labor": return o.nonLaborCostInlineEnabled;
    case "space": return o.spaceQuantityInlineEnabled;
    case "stock": return o.stockQuantityInlineEnabled;
    default: return false;
  }
};
